// Sign-in failure types (T09). Each one gets its own plain-English message on the login page,
// so a helper knows whether to retry, check the network or ask Arturo.
import { ApiError, NetworkError } from '@cis/api-client';

export const LOGIN_ERROR_KINDS = [
  'not_assigned',
  'not_provisioned',
  'account_mismatch',
  'expired',
  'rate_limited',
  'network',
  'cancelled',
  'server',
  'config',
  'unknown',
  'session_ended',
] as const;

export type LoginErrorKind = (typeof LOGIN_ERROR_KINDS)[number];

export const LOGIN_ERROR_MESSAGES: Readonly<Record<LoginErrorKind, string>> = {
  not_assigned:
    'Your Microsoft account is not assigned to CIS Program Portal. Ask Arturo to give you access, then try again.',
  not_provisioned:
    'You signed in with Microsoft, but you do not have a portal account yet. Ask Arturo to set one up for you.',
  account_mismatch:
    'This Microsoft account does not match the portal account on file for your email. Tell Arturo.',
  expired: 'This sign-in took too long or was already used. Select "Sign in with Microsoft" again.',
  rate_limited: 'Too many sign-in attempts from your network. Wait a minute, then try again.',
  network: 'We could not reach the sign-in service. Check your internet connection and try again.',
  cancelled: 'Sign-in was cancelled. Select "Sign in with Microsoft" to try again.',
  server:
    'The portal could not start your session. Try again in a few minutes. If it keeps happening, tell Arturo.',
  config: 'Sign-in is not set up for this site yet. Tell Arturo.',
  unknown: 'Sign-in did not work. Try again. If it keeps happening, tell Arturo.',
  // Not a sign-in failure: the portal session ended (1 h idle, 8 h at most, or revoked) (D-R19).
  session_ended:
    'Your portal session ended, so you were signed out. This happens after an hour without activity or eight hours in total. Sign in again to carry on.',
};

export function isLoginErrorKind(value: unknown): value is LoginErrorKind {
  return typeof value === 'string' && (LOGIN_ERROR_KINDS as readonly string[]).includes(value);
}

/** Thrown before any redirect when the build has no Entra tenant or client id. */
export class LoginConfigError extends Error {
  override readonly name = 'LoginConfigError';
}

// Entra AADSTS50105: the user is not assigned to the app (Assignment required = Yes).
// Matches "AADSTS50105: The signed in user is not assigned to a role"; rejects "AADSTS501051".
const NOT_ASSIGNED_RE = /AADSTS50105\b/;
// Entra AADSTS65004: the user declined consent on the Microsoft page.
// Matches "AADSTS65004: User declined to consent"; rejects "AADSTS650041".
const CANCELLED_RE = /AADSTS65004\b/;

const HTTP_UNAUTHORIZED = 401;
const HTTP_TOO_MANY = 429;
const HTTP_SERVER_ERROR_MIN = 500;

// The API's refusals at POST /auth/session (review D-R8): `not_provisioned` (no active users row),
// `forbidden` with details.reason `missing_app_role` (no CIS.Team/CIS.Leads role in the token) or
// `identity_mismatch` (entra_oid differs), 401 for a token or nonce it will not accept, 429 from
// the /auth rate limit. Any other 403, such as `origin_rejected`, is not the person's access.
const FORBIDDEN_REASONS: ReadonlyMap<string, LoginErrorKind> = new Map([
  ['missing_app_role', 'not_assigned'],
  ['identity_mismatch', 'account_mismatch'],
]);

const MSAL_NETWORK_CODES = new Set([
  'no_network_connectivity',
  'post_request_failed',
  'get_request_failed',
  'endpoints_resolution_error',
  'network_error',
  'openid_config_error',
  'timed_out',
]);
const MSAL_CANCELLED_CODES = new Set(['user_cancelled', 'access_denied']);

interface MsalLikeError {
  readonly errorCode: string;
  readonly errorMessage: string;
  readonly subError: string;
}

function asMsalError(err: unknown): MsalLikeError | null {
  if (typeof err !== 'object' || err === null || !('errorCode' in err)) return null;
  const e = err as Partial<Record<keyof MsalLikeError, unknown>>;
  const text = (v: unknown): string => (typeof v === 'string' ? v : '');
  return {
    errorCode: text(e.errorCode),
    errorMessage: text(e.errorMessage),
    subError: text(e.subError),
  };
}

function classifyApiError(err: ApiError): LoginErrorKind {
  if (err.code === 'not_provisioned') return 'not_provisioned';
  if (err.code === 'forbidden') return FORBIDDEN_REASONS.get(err.reason ?? '') ?? 'unknown';
  if (err.status === HTTP_UNAUTHORIZED) return 'expired';
  if (err.status === HTTP_TOO_MANY) return 'rate_limited';
  if (err.status >= HTTP_SERVER_ERROR_MIN) return 'server';
  // A 2xx that was not the session JSON (e.g. the SPA page from a misrouted /api): D-R10.
  if (err.code === 'unexpected_response') return 'server';
  return 'unknown';
}

function classifyMsalError(err: MsalLikeError): LoginErrorKind {
  const text = `${err.errorMessage} ${err.subError}`;
  if (NOT_ASSIGNED_RE.test(text)) return 'not_assigned';
  if (CANCELLED_RE.test(text) || MSAL_CANCELLED_CODES.has(err.errorCode)) return 'cancelled';
  if (MSAL_NETWORK_CODES.has(err.errorCode)) return 'network';
  return 'unknown';
}

/** Maps anything thrown during sign-in (MSAL, the session API, fetch, config) to one failure type. */
export function classifyLoginError(err: unknown): LoginErrorKind {
  if (err instanceof LoginConfigError) return 'config';
  if (err instanceof ApiError) return classifyApiError(err);
  // Only the api-client's wrapped fetch rejection is network trouble; a bare TypeError is a bug.
  if (err instanceof NetworkError) return 'network';
  const msal = asMsalError(err);
  return msal === null ? 'unknown' : classifyMsalError(msal);
}
